import { gql } from "@apollo/client";

import useUser from "@src/features/app/hooks/useUser";
import { useToast } from "@src/lib/hooks/useToast";
import {
  ChatType,
  UserPushNotificationChatSetting,
  UserPushNotificationSettingType,
} from "@src/types.generated";

import {
  ChatNotificationOverrideDocument,
  ChatNotificationOverrideQuery,
  ChatNotificationOverrideQueryVariables,
  useChatNotificationOverrideQuery,
  useChatsPushNotificationSettingQuery,
  useDeleteChatNotificationOverrideMutation,
  useSetChatNotificationOverrideMutation,
} from "./useChatNotificationOverride.generated";
import { isDirectMessage } from "./utils";

export const DEFAULT_CHATS_STATE = UserPushNotificationChatSetting.NewMessages;

export type ChatNotificationTarget = {
  id: string;
  chatType: ChatType;
  members: { id: number }[];
  owners: { id: number }[];
};

export function useMutedChatIds(): Set<string> {
  const { data } = useChatNotificationOverrideQuery();
  return new Set(
    (data?.UserPushNotificationSettingChatOverrides ?? [])
      .filter((o) => o.chatSetting === UserPushNotificationChatSetting.Muted)
      .map((o) => o.chat.id),
  );
}

function useUserChatsDefault(
  chat: ChatNotificationTarget,
): UserPushNotificationChatSetting {
  const { user } = useUser();
  const { data } = useChatsPushNotificationSettingQuery();
  const settingType = isDirectMessage(chat)
    ? UserPushNotificationSettingType.OneOnOneChats
    : UserPushNotificationSettingType.Chats;
  const chatsSetting = data?.UserPushNotificationSettings.find(
    (s) => s.type === settingType,
  );
  // Admins who can see a chat without belonging to it default to muted.
  // `viewerIsAdminOrChatOwner` is deliberately not consulted: it is true for
  // every org admin on every group message, which would defeat the rule.
  const isParticipant =
    user != null &&
    (chat.members.some((member) => member.id === user.id) ||
      chat.owners.some((owner) => owner.id === user.id));
  if (!isParticipant) {
    return UserPushNotificationChatSetting.Muted;
  }
  return chatsSetting?.chatSetting ?? DEFAULT_CHATS_STATE;
}

export function useChatNotificationOverride(chat: ChatNotificationTarget): {
  overrideChatSetting: UserPushNotificationChatSetting | null;
  userChatsDefault: UserPushNotificationChatSetting;
  effectiveChatSetting: UserPushNotificationChatSetting;
  muted: boolean;
  setChatSetting: (next: UserPushNotificationChatSetting) => void;
  resetChatSetting: () => void;
} {
  const { addErrorToast } = useToast();
  const chatId = chat.id;
  const userChatsDefault = useUserChatsDefault(chat);
  const { data } = useChatNotificationOverrideQuery();
  const override = data?.UserPushNotificationSettingChatOverrides.find(
    (o) => o.chat.id === chatId,
  );
  const overrideChatSetting = override?.chatSetting ?? null;
  const effectiveChatSetting = overrideChatSetting ?? userChatsDefault;
  const muted = effectiveChatSetting === UserPushNotificationChatSetting.Muted;

  const [setOverrideMutation] = useSetChatNotificationOverrideMutation({
    onError: (error) => {
      addErrorToast({ ...error, callsite: "chat_notification_override" });
    },
  });
  const [deleteOverrideMutation] = useDeleteChatNotificationOverrideMutation({
    onError: (error) => {
      addErrorToast({ ...error, callsite: "chat_notification_override" });
    },
  });

  const setChatSetting = (next: UserPushNotificationChatSetting) => {
    if (next === effectiveChatSetting) return;
    if (override && next === userChatsDefault) {
      const overrideId = override.id;
      deleteOverrideMutation({
        variables: { id: overrideId },
        update: (cache, { data: mutationData }) => {
          if (
            !mutationData?.deleteUserPushNotificationSettingChatOverride.success
          ) {
            return;
          }
          cache.updateQuery<
            ChatNotificationOverrideQuery,
            ChatNotificationOverrideQueryVariables
          >({ query: ChatNotificationOverrideDocument }, (existing) => {
            const overrides =
              existing?.UserPushNotificationSettingChatOverrides ?? [];
            return {
              UserPushNotificationSettingChatOverrides: overrides.filter(
                (o) => o.id !== overrideId,
              ),
            };
          });
        },
      });
      return;
    }
    setOverrideMutation({
      variables: { input: { chatId, chatSetting: next } },
      update: (cache, { data: mutationData }) => {
        const updated =
          mutationData?.updateOrCreateUserPushNotificationSettingChatOverride
            .userPushNotificationSettingChatOverride;
        if (!updated) return;
        cache.updateQuery<
          ChatNotificationOverrideQuery,
          ChatNotificationOverrideQueryVariables
        >({ query: ChatNotificationOverrideDocument }, (existing) => {
          const overrides =
            existing?.UserPushNotificationSettingChatOverrides ?? [];
          const hasIt = overrides.some((o) => o.id === updated.id);
          return {
            UserPushNotificationSettingChatOverrides: hasIt
              ? overrides.map((o) => (o.id === updated.id ? updated : o))
              : [...overrides, updated],
          };
        });
      },
    });
  };

  const resetChatSetting = () => {
    if (!override) return;
    const overrideId = override.id;
    deleteOverrideMutation({
      variables: { id: overrideId },
      update: (cache, { data: mutationData }) => {
        if (
          !mutationData?.deleteUserPushNotificationSettingChatOverride.success
        ) {
          return;
        }
        cache.updateQuery<
          ChatNotificationOverrideQuery,
          ChatNotificationOverrideQueryVariables
        >({ query: ChatNotificationOverrideDocument }, (existing) => {
          const overrides =
            existing?.UserPushNotificationSettingChatOverrides ?? [];
          return {
            UserPushNotificationSettingChatOverrides: overrides.filter(
              (o) => o.id !== overrideId,
            ),
          };
        });
      },
    });
  };

  return {
    overrideChatSetting,
    userChatsDefault,
    effectiveChatSetting,
    muted,
    setChatSetting,
    resetChatSetting,
  };
}

gql`
  query ChatNotificationOverride {
    UserPushNotificationSettingChatOverrides {
      id
      chatSetting
      chat {
        id
      }
    }
  }

  query ChatsPushNotificationSetting {
    UserPushNotificationSettings {
      id
      type
      chatSetting
    }
  }

  mutation SetChatNotificationOverride(
    $input: UpdateOrCreateUserPushNotificationSettingChatOverrideInput!
  ) {
    updateOrCreateUserPushNotificationSettingChatOverride(input: $input) {
      success
      error {
        message
      }
      userPushNotificationSettingChatOverride {
        id
        chatSetting
        chat {
          id
        }
      }
    }
  }

  mutation DeleteChatNotificationOverride($id: UUID!) {
    deleteUserPushNotificationSettingChatOverride(id: $id) {
      success
      error {
        message
      }
    }
  }
`;
